<template>
  <q-table
    v-bind="$attrs"
    :columns="appointmentColumns"
    :rows="appointments"
  >
    <template
      v-if="noDataLabel"
      #no-data
    >
      <div class="full-width row items-center text-grey">
        <q-icon
          name="warning"
          size="sm"
          class="q-mr-sm"
        />
        <span>{{ noDataLabel }}</span>
      </div>
    </template>
    <template #body-selection="props">
      <q-checkbox
        :model-value="selected"
        :val="props.row"
        :disable="isInactive(props.row.status)"
        @update:model-value="onSelect(props.row)"
      >
        <q-tooltip
          v-if="isInactive(props.row.status)"
        >
          {{ getStatusTooltip(props.row.status) }}
        </q-tooltip>
      </q-checkbox>
    </template>
    <template #body-cell-scheduledStartTimestamp="props">
      <q-td
        class="vertical-middle"
        :props="props"
      >
        <span
          v-if="isInactive(props.row.status)"
          class="q-col-auto q-pr-sm"
        >
          <q-icon
            name="cancel"
            color="negative"
            size="xs"
          />
        </span>
        <span
          class="q-col-auto"
          :class="{'text-strike': isInactive(props.row.status)}"
        >{{ props.value }}</span>
        <q-tooltip
          v-if="isInactive(props.row.status)"
        >
          {{ getStatusTooltip(props.row.status) }}
        </q-tooltip>
      </q-td>
    </template>
  </q-table>
</template>
<script>
import { Timestamp } from 'firebase/firestore';
import { get, includes } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import { BASE_INTEGRATIONS } from 'src/constants/integrations';
import Integrations from 'src/mixins/Integrations';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';

export default {
  name: 'SimpleAppointmentsTable',

  mixins: [Integrations, Toolbox],

  props: {
    appointments: {
      type: Array,
      default: () => ([]),
    },
    selected: {
      type: Array,
      default: () => ([]),
    },
    noDataLabel: {
      type: String,
      default: null,
    },
  },

  emits: ['update:selected'],

  setup() {
    const { getLocaleDateString } = useTime();
    return { getLocaleDateString };
  },

  data() {
    return {
      defaultAppointmentColumns: [
        {
          name: 'scheduledStartTimestamp',
          label: this.$t('appointments.scheduledStartTimestamp'),
          align: 'left',
          field: 'scheduledStartTimestamp',
          format: (startTs) => ((startTs instanceof Timestamp)
            ? this.getLocaleDateString(startTs, { options: { hour: 'numeric', minute: '2-digit' } }) : ''),
          sortable: true,
        },
        {
          name: 'duration',
          label: this.$t('appointments.duration'),
          align: 'left',
          field: 'duration',
          format: (duration) => (this.formatDuration(duration)),
          sortable: true,
        },
        {
          name: 'userName',
          label: this.$t('appointments.userName'),
          align: 'left',
          field: 'userName',
          sortable: true,
        },
        {
          name: 'sessionName',
          label: this.$t('appointments.sessionName'),
          align: 'left',
          field: 'sessionName',
          sortable: false,
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic']),

    appointmentColumns() {
      if (this.connectedClinic.integration) {
        return [
          ...this.defaultAppointmentColumns,
          {
            name: 'integrationReference',
            label: this.$t(
              'appointments.integrationReference',
              { integration: this.getIntegrationName(this.connectedClinic.integration) },
            ),
            align: 'left',
            field: (appointment) => {
              return BASE_INTEGRATIONS.includes(this.connectedClinic.integration)
                ? appointment.integrationReference
                : appointment.integrationData?.references?.appointmentId;
            },
            sortable: false,
          },
        ];
      }
      return this.defaultAppointmentColumns;
    },
  },

  methods: {
    onSelect(selectedValue) {
      if (includes(this.selected, selectedValue)) {
        this.$emit('update:selected', []);
        return;
      }

      this.$emit('update:selected', [selectedValue]);
    },

    isInactive(status) {
      return ['cancelled', 'deleted'].includes(status);
    },

    getStatusTooltip(status) {
      const statuses = this.$t('appointments.status');
      return get(statuses, status, null);
    },
  },
};
</script>
